<template>
  <div class="flex w-full h-full max-w-[1600px] mx-auto space-x-4 p-4 pb-6 print:block print:h-auto print:w-full print:max-w-none print:m-0 print:p-0 print:space-x-0 font-sans">
    
    <!-- 极左侧：企划会话历史 -->
    <div :class="isSidebarOpen ? 'w-64' : 'w-16 items-center'" class="bg-white/80 backdrop-blur-md rounded-xl p-4 flex flex-col transition-all duration-300 relative border border-gray-200 shadow-sm flex-shrink-0 z-20 print:hidden">
      
      <div class="flex items-center mb-6" :class="isSidebarOpen ? 'justify-start' : 'justify-center'">
        <button @click="isSidebarOpen = !isSidebarOpen" class="text-gray-600 hover:text-archGold p-1.5 rounded-md hover:bg-gray-100 transition" title="收起/展开">
          <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
      </div>

      <div v-show="isSidebarOpen" class="flex flex-col h-full overflow-hidden transition-opacity duration-200">
        <button @click="startNewChat" class="w-full bg-archGold/10 border border-archGold/50 text-archGold font-bold py-2.5 rounded-full hover:bg-archGold hover:text-white transition flex justify-center items-center space-x-2 mb-6">
          <span class="text-lg leading-none">+</span> <span>新对话</span>
        </button>

        <div class="relative mb-4">
          <input v-model="historySearchQuery" type="text" placeholder="搜索历史对话..." class="w-full bg-gray-50 border border-gray-200 rounded-md py-1.5 pl-8 pr-2 text-xs text-gray-700 font-medium focus:outline-none focus:border-archGold focus:bg-white" />
          <span class="absolute left-2 top-1.5 text-gray-400 text-xs">🔍</span>
        </div>
        
        <h4 class="text-gray-400 text-xs font-bold mb-3 tracking-widest border-b border-gray-200 pb-2">近期对话</h4>
        
        <div class="flex-grow overflow-y-auto custom-scrollbar space-y-1 pr-1">
          <div v-if="filteredSessions.length === 0" class="text-center text-xs text-gray-400 font-medium mt-4">暂无记录</div>
          
          <!-- 对话记录列表项 -->
          <div 
            v-for="session in filteredSessions" :key="session.id" 
            class="group px-3 py-2 text-sm rounded-md cursor-pointer transition flex items-center justify-between"
            :class="currentSessionId === session.id ? 'bg-archGold/10 text-archGold font-bold' : 'text-gray-600 hover:bg-gray-100'"
          >
            <!-- 点击左侧区域加载对话 -->
            <div class="flex items-center space-x-2 truncate flex-grow" @click="loadSession(session)">
              <span class="opacity-70">💬</span> <span class="truncate">{{ session.title }}</span>
            </div>
            
            <!-- 点击右侧叉叉删除对话 -->
            <button @click.stop="deleteSession(session.id)" class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition px-1" title="删除记录">
              <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <div v-show="!isSidebarOpen" class="flex flex-col items-center space-y-4">
         <button @click="startNewChat" class="text-archGold hover:text-white p-2.5 rounded-full bg-archGold/10 border border-archGold/30 hover:bg-archGold transition shadow-sm" title="新建对话">
          <span class="text-xl leading-none font-bold">+</span>
        </button>
      </div>
    </div>

    <!-- 中间：AI 交互面板 -->
    <div class="flex-1 flex flex-col bg-white/90 backdrop-blur-md rounded-xl overflow-hidden shadow-sm border border-gray-200 z-10 relative print:hidden">
      
      <div class="flex-grow p-6 overflow-y-auto custom-scrollbar space-y-6">
        <div v-if="chatHistory.length === 0" class="flex flex-col items-center justify-center h-full text-center">
          <div class="w-16 h-16 rounded-full bg-archGold/10 flex items-center justify-center text-archGold text-3xl mb-4">✨</div>
          <h4 class="text-gray-900 font-title-heavy text-xl mb-2">AI 设计助理</h4>
          <p class="text-sm text-gray-500 font-medium mb-8 max-w-sm leading-relaxed">输入您的项目需求，我将为您梳理出专业完整的设计概念企划书。</p>
          <div class="w-full max-w-md grid grid-cols-1 gap-3">
            <button v-for="prompt in ['山脚下36个班级的小学校设计', '旧城区老厂房商业改造策划']" :key="prompt" @click="handlePromptClick(prompt)" class="px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 font-medium text-sm hover:border-archGold/50 hover:bg-archGold/5 transition text-left shadow-sm">
              {{ prompt }}
            </button>
          </div>
        </div>

        <template v-else>
          <div v-for="(msg, index) in chatHistory" :key="index" class="flex flex-col space-y-2" :class="msg.role === 'user' ? 'items-end' : 'items-start'">
            <div v-if="msg.role === 'ai'" class="flex items-center space-x-2 mb-1 pl-1">
               <div class="w-6 h-6 rounded-full bg-archGold flex items-center justify-center text-white text-xs shadow-sm">🤖</div>
               <span class="text-xs text-gray-500 font-bold">AI 设计助理</span>
            </div>

            <!-- 用户气泡与 AI 气泡颜色适配浅色 -->
            <div :class="msg.role === 'user' ? 'bg-gradient-to-br from-archGold to-[#B89053] text-white max-w-[80%] shadow-md' : 'bg-white text-gray-800 w-[95%] border border-gray-200 shadow-sm'" class="rounded-2xl px-5 py-4 text-sm leading-relaxed">
              <p v-if="msg.role === 'user'" class="font-medium">{{ msg.content }}</p>
              
              <!-- AI 回复内容 -->
              <div v-else>
                 <div v-if="msg.status === 'generating'" class="flex items-center text-archGold">
                  <svg class="animate-spin h-4 w-4 mr-2" xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  <span class="font-bold">分析处理中...</span>
                 </div>
                 <div v-else>
                    <p class="font-bold text-gray-900 mb-3">{{ msg.content }}</p>
                    <ul class="space-y-2 text-gray-600 font-medium mt-4 border-t border-gray-100 pt-4">
                      <li v-for="(item, idx) in msg.details" :key="idx" class="flex items-center">
                        <span class="text-green-500 mr-2 text-lg font-bold">✓</span>{{ item }}
                      </li>
                    </ul>
                 </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 底部输入框适配浅色 -->
      <div class="p-6 pt-2 bg-transparent">
        <div class="relative bg-white border border-gray-300 rounded-2xl focus-within:border-archGold focus-within:shadow-[0_0_10px_rgba(212,175,55,0.15)] transition-all shadow-sm">
          <textarea 
            v-model="inputText" 
            @keydown.enter.prevent="handlePromptClick(inputText)" 
            placeholder="继续输入您的修改意见或补充细节..." 
            class="w-full bg-transparent p-4 pr-12 text-gray-800 font-medium text-sm focus:outline-none resize-none h-14 custom-scrollbar" 
            :disabled="isGenerating"
          ></textarea>
          <button @click="handlePromptClick(inputText)" :disabled="isGenerating || !inputText.trim()" class="absolute right-3 bottom-3 w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-archGold hover:text-white disabled:opacity-30 transition">
             <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>
          </button>
        </div>
        <p class="text-center text-[10px] text-gray-400 font-medium mt-2">AI 生成内容可能存在误差，请核实后使用</p>
      </div>
    </div>

    <!-- 右侧：生成的策划文档区 -->
    <div class="w-[40%] bg-[#fafafa] rounded-xl overflow-hidden shadow-sm flex flex-col relative border border-gray-200 z-10 print:w-full print:border-none print:shadow-none print:bg-white print:m-0 print:p-0">
      
      <div class="p-4 border-b border-gray-200 flex justify-between items-center bg-white shadow-sm z-10 print:hidden">
        <h3 class="font-bold text-gray-800 flex items-center space-x-2"><span class="text-xl">📄</span> <span>生成的企划方案</span></h3>
        <div v-if="docGenerated" class="flex space-x-3">
          <button @click="copyDoc" class="border border-gray-300 text-gray-600 font-bold px-3 py-1.5 rounded text-xs hover:bg-gray-100 transition flex items-center space-x-1">
             <span>{{ copyText }}</span>
          </button>
          <button @click="exportPDF" class="bg-yellow-500 text-white px-3 py-1.5 rounded text-xs font-bold shadow-sm hover:bg-yellow-600 transition flex items-center space-x-1">
             <span>导出 PDF</span>
          </button>
        </div>
      </div>

      <div v-if="!docGenerated && !isGenerating" class="flex-grow flex flex-col items-center justify-center text-gray-400 bg-gray-50 print:hidden">
        <span class="text-4xl mb-4 opacity-50">📄</span><p class="text-sm font-medium">策划结果将在此处自动生成</p>
      </div>

      <div v-else-if="isGenerating" class="flex-grow flex flex-col items-center justify-center text-archGold bg-gray-50 print:hidden">
         <div class="animate-pulse flex flex-col items-center">
            <div class="w-12 h-16 bg-gray-200 rounded mb-4 relative overflow-hidden">
               <div class="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-transparent animate-[shimmer_1.5s_infinite]"></div>
            </div>
            <span class="text-sm text-gray-500 font-bold">文档生成中...</span>
         </div>
      </div>

      <!-- 核心打印区域 -->
      <div v-else id="printable-doc" ref="docContent" class="flex-grow p-10 overflow-y-auto text-gray-800 text-sm leading-relaxed custom-scrollbar bg-white print:overflow-visible print:p-0 print:m-0">
        <h1 class="text-3xl font-title-heavy text-gray-900 mb-8 border-b-2 border-gray-200 pb-4">{{ currentTitle }} - 概念企划书</h1>
        <h2 class="text-lg font-bold text-gray-900 mb-3 mt-6"># 1. 场地分析与回应</h2>
        <ul class="list-disc pl-6 mb-6 space-y-2 text-gray-700 font-medium">
          <li><strong>地形条件</strong>: 充分利用自然高差，采用退台式设计，减少土方工程量，实现建筑与山体的有机融合。</li>
          <li><strong>气候适应</strong>: 建筑主朝向设定为南偏东15度，以获取最优采光；北侧设置实体墙面并加强保温层，抵御冬季寒风。</li>
        </ul>
        <h2 class="text-lg font-bold text-gray-900 mb-3 mt-6"># 2. 功能配比策略</h2>
        <ul class="list-disc pl-6 mb-6 space-y-2 text-gray-700 font-medium">
          <li><strong>核心功能区 (40%)</strong>: 保证最佳采光和安静环境，满足核心需求。</li>
          <li><strong>公共共享区 (30%)</strong>: 设置在交通枢纽位置，方便内部流线交汇，激活社区活力。</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { globalState } from '../store';

interface Session {
  id: string;
  title: string;
  history: {role: string, content: string, status?: string, details?: string[]}[];
}

const isSidebarOpen = ref(true);
const historySearchQuery = ref('');
const allSessions = ref<Session[]>([]);
const currentSessionId = ref<string>('');

const getStorageKey = () => {
  return globalState.currentUser ? `arch_planning_${globalState.currentUser}` : 'arch_planning_guest';
};

onMounted(() => {
  const saved = localStorage.getItem(getStorageKey());
  if (saved) {
    allSessions.value = JSON.parse(saved);
  }
});

const filteredSessions = computed(() => {
  if (!historySearchQuery.value.trim()) return allSessions.value;
  return allSessions.value.filter(s => s.title.includes(historySearchQuery.value));
});

const inputText = ref('');
const chatHistory = ref<{role: string, content: string, status?: string, details?: string[]}[]>([]);
const isGenerating = ref(false);
const docGenerated = ref(false);
const currentTitle = ref('');
const docContent = ref<HTMLElement | null>(null);
const copyText = ref('复制');

const startNewChat = () => {
  chatHistory.value = [];
  docGenerated.value = false;
  isGenerating.value = false;
  currentTitle.value = '';
  currentSessionId.value = '';
  inputText.value = '';
};

const loadSession = (session: Session) => {
  currentSessionId.value = session.id;
  currentTitle.value = session.title;
  chatHistory.value = [...session.history];
  docGenerated.value = true;
  isGenerating.value = false;
};

const deleteSession = (id: string) => {
  allSessions.value = allSessions.value.filter(s => s.id !== id);
  localStorage.setItem(getStorageKey(), JSON.stringify(allSessions.value));
  
  if (currentSessionId.value === id) {
    startNewChat(); // 如果删除的是当前正在查看的会话，重置界面
  }
};

const handlePromptClick = (text: string) => {
  if (!text.trim() || isGenerating.value) return;
  
  const isFollowUp = chatHistory.value.length > 0;
  chatHistory.value.push({ role: 'user', content: text });
  if (!currentTitle.value) currentTitle.value = text;
  
  inputText.value = '';
  
  if (!isFollowUp) {
    chatHistory.value.push({ role: 'ai', content: `正在深度分析并撰写企划案...`, status: 'generating' });
  } else {
    chatHistory.value.push({ role: 'ai', content: `正在根据您的反馈更新方案...`, status: 'generating' });
  }
  
  isGenerating.value = true;
  docGenerated.value = false;

  setTimeout(() => {
    const lastMsg = chatHistory.value[chatHistory.value.length - 1];
    lastMsg.status = 'done';
    
    if (!isFollowUp) {
      lastMsg.content = `分析完成！已为您生成"${currentTitle.value}"的概念企划书，请在右侧查阅。`;
      lastMsg.details = ['场地条件与文脉分析', '建筑空间与功能配比推演', '生成概念意向与造型参考'];
    } else {
      lastMsg.content = `方案已更新！已根据您的要求修改了企划书，请查阅。`;
      lastMsg.details = ['分析追加设计需求', '重新梳理功能空间', '优化交通流线组织', '更新概念企划书方案'];
    }
    
    isGenerating.value = false;
    docGenerated.value = true;

    if (currentSessionId.value) {
      const idx = allSessions.value.findIndex(s => s.id === currentSessionId.value);
      if (idx !== -1) allSessions.value[idx].history = [...chatHistory.value];
    } else {
      const newSession: Session = {
        id: Date.now().toString(),
        title: currentTitle.value.length > 12 ? currentTitle.value.substring(0, 12) + '...' : currentTitle.value,
        history: [...chatHistory.value]
      };
      allSessions.value.unshift(newSession);
      currentSessionId.value = newSession.id;
    }
    localStorage.setItem(getStorageKey(), JSON.stringify(allSessions.value));

  }, 2000);
};

const copyDoc = async () => {
  if (!docContent.value) return;
  try {
    await navigator.clipboard.writeText(docContent.value.innerText);
    copyText.value = '已复制';
    setTimeout(() => copyText.value = '复制', 2000);
  } catch (err) {
    alert('复制失败，请手动选择复制');
  }
};

const exportPDF = () => {
  window.print();
};
</script>

<style scoped>
@keyframes shimmer {
  100% {
    transform: translateY(100%);
  }
}
</style>